import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { ShieldCheck, Lock, KeyRound, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

const inputC = "w-full rounded-xl border border-slate-800 bg-slate-900/80 pl-10 pr-3 py-3 text-base sm:text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const btnC = "flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60";

function readToken() {
  const t = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") || "";
  if (t) window.history.replaceState(null, "", window.location.pathname);
  return t;
}

function Field({ icon: Icon, ...p }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input {...p} className={inputC} />
    </div>
  );
}

function ResetForm({ token, onDone, assisted }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 12) { setErr("Password must be at least 12 characters"); return; }
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/platform/auth/reset-password", { token, new_password: pw, ...(assisted ? {} : useRecovery ? { recovery_code: code } : { otp: code }) });
      onDone("success");
    } catch (ex) {
      const msg = apiErr(ex);
      if (ex?.response?.status === 400 && /invalid or has expired/i.test(msg)) onDone("invalid"); else setErr(msg);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="pa-reset-form">
      <Field icon={Lock} data-testid="pa-reset-password" type={showPw ? "text" : "password"} autoComplete="new-password" aria-label="New password" required placeholder="New password (12+ characters, letters and numbers)" value={pw} onChange={(e) => setPw(e.target.value)} />
      <Field icon={Lock} data-testid="pa-reset-password-confirm" type={showPw ? "text" : "password"} autoComplete="new-password" aria-label="Confirm new password" required placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
      <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" data-testid="pa-reset-show-password" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} /> Show passwords</label>
      {assisted ? (
        <p data-testid="pa-reset-assisted" className="rounded-lg border border-emerald-900 bg-emerald-950/40 p-2 text-xs text-emerald-200">Administrator-assisted recovery is active for this account. No authenticator code is needed here; you will set up a new authenticator right after signing in.</p>
      ) : (<>
      <Field icon={KeyRound} data-testid="pa-reset-code" autoComplete="one-time-code" aria-label={useRecovery ? "Recovery code" : "Authenticator code"} required placeholder={useRecovery ? "Recovery code" : "6-digit authenticator code"} value={code} onChange={(e) => setCode(e.target.value)} inputMode={useRecovery ? "text" : "numeric"} />
      <button type="button" data-testid="pa-reset-toggle-recovery" onClick={() => { setUseRecovery(!useRecovery); setCode(""); }} className="w-full text-center text-xs text-slate-400 hover:text-emerald-300">
        {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
      </button>
      </>)}
      {err && <p data-testid="pa-reset-error" className="rounded-lg border border-rose-900 bg-rose-950/40 p-2 text-xs text-rose-200">{err}</p>}
      <button data-testid="pa-reset-submit" disabled={busy} type="submit" className={btnC}>{busy ? "Saving…" : "Set new password"} <ArrowRight className="h-4 w-4" /></button>
    </form>
  );
}

export default function PlatformResetPassword() {
  const [token] = useState(readToken);
  const [state, setState] = useState("checking");
  const [assisted, setAssisted] = useState(false);
  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.post("/platform/auth/reset-password/validate", { token })
      .then((r) => { setAssisted(!!r.data.assisted); setState(r.data.valid ? "form" : "invalid"); }).catch(() => setState("invalid"));
  }, [token]);
  return (
    <div className="min-h-[100dvh] overflow-x-hidden bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-8 sm:p-6">
      <div className="w-full max-w-md rounded-3xl border border-slate-800/80 bg-slate-900/40 p-6 shadow-2xl backdrop-blur-xl sm:p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/15 grid place-items-center"><ShieldCheck className="h-5 w-5 text-emerald-400" /></div>
          <div className="font-display text-lg font-extrabold tracking-tight">Pawkt Platform</div>
        </div>
        {state === "checking" && <p className="text-sm text-slate-400" data-testid="pa-reset-checking">Checking your reset link…</p>}
        {state === "form" && (<><h1 className="font-display text-2xl font-bold">Choose a new password</h1>
          <p className="text-sm text-slate-400 mt-1">Confirm with your authenticator app or a recovery code.</p><ResetForm token={token} onDone={setState} assisted={assisted} /></>)}
        {state === "invalid" && (
          <div data-testid="pa-reset-invalid"><h1 className="font-display text-2xl font-bold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" /> Link expired</h1>
            <p className="text-sm text-slate-400 mt-1">This reset link is invalid, already used or has expired. Request a new one from the sign-in page.</p></div>
        )}
        {state === "success" && (
          <div data-testid="pa-reset-success"><h1 className="font-display text-2xl font-bold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Password changed</h1>
            <p className="text-sm text-slate-400 mt-1">All sessions were signed out. Sign in with your new password and MFA.</p></div>
        )}
        {state !== "checking" && <Link to="/platform-admin/login" data-testid="pa-reset-signin" className="mt-6 block text-center text-xs text-slate-400 hover:text-emerald-300">Go to Platform Admin sign in</Link>}
      </div>
    </div>
  );
}
